
interface AppInterface {
  busAlerts: Alert[];
}

import '@/assets/css/app.css';

import Vue, { VueConstructor } from 'vue';
import LeftNav from '@/navigation/LeftNav.vue';
import TopNav from '@/navigation/TopNav.vue';
import NotificationPanel from '@/notifications/NotificationPanel.vue';
import Alert from '@/eventbus/alert';
import { bus } from '@/main';
import { AxiosError } from 'axios';
import { mapMutations, mapState, mapActions } from 'vuex';
import { IdTokenClaims } from './auth/idTokenClaims';
import ConfigState from './store/config/configState';
import UserState from './store/user/userState';
import ResponseError from './models/responseError';
import ResponseErrors from './models/responseErrors';
import { ThemeManager } from './plugins/themeManager';

const App = (Vue as VueConstructor<Vue & AppInterface & ConfigState & UserState>).extend({
  name: 'App',

  components: {
    'left-nav': LeftNav,
    'top-nav': TopNav,
    'notification-panel': NotificationPanel,
  },

  async created() {
    this.$auth.addListener('loginEvent', this.signedIn);
    this.$auth.checkSignedIn().then(
      () => {
        this.signedIn();
      },
      () => {
        if (!this.isLoginOrCallback) {
          this.$router.push({ path: '/login' });
        }
      },
    );

    const themeManager = ThemeManager.getInstance();
    this.$vuetify.theme.dark = themeManager.getCurrentTheme();

    this.$root.$on('theme-changed', (isDark: boolean) => {
      this.$vuetify.theme.dark = isDark;
    });
  },

  computed: {
    ...mapState<UserState>('userStore', {
      user: 'user',
    }),
    ...mapState<ConfigState>('configStore', {
      activityDefinitions: 'activityDefinitions',
      workflows: 'workflows',
    }),
    isLoginOrCallback() {
      return this.$route.path.startsWith('/login') || this.$route.path.startsWith('/callback');
    },
  },

  data() {
    return {
      expanded: false,
      xsNavVisible: false,
      mini: true,
      drawer: true,
      menuVisible: false,
      isAuthenticated: false,
      busAlerts: [] as Alert[],
      toastAlerts: [] as Alert[],
      showNotificationPanel: false,
      newMpCount: 0 as number,
      newLightningCount: 0 as number,
      newLightningCatalogCount: 0 as number,
      newFieldOrderzCount: 0 as number,
    };
  },

  methods: {
    ...mapActions('configStore', ['getOrchestrationRouteModels', 'getWorkflows']),
    ...mapActions('activityDefinitionStore', ['getActivityDefinitions']),
    ...mapActions('userStore', ['checkEditorStatus', 'checkQueueEditorStatus']),
    ...mapMutations('userStore', ['updateProfile']),

    handleAlert(
      message: string,
      color?: string,
      timeout?: number,
      error?: AxiosError<ResponseError>,
      showToast?: boolean,
    ) {
      const alert = new Alert();
      alert.showToast = showToast || false;
      alert.message = message;
      alert.error = error;
      const existingAlert = this.toastAlerts.find((x) => x.message.indexOf(alert.message) > -1);
      if (!existingAlert) {
        if (error) {
          if (typeof error === 'string') {
            alert.message = alert.message + ' ' + error;
          } else {
            if (
              error.response !== null &&
              error.response !== undefined &&
              error.response.data !== null &&
              error.response.data !== undefined &&
              typeof error.response.data === 'string'
            ) {
              // check general server errors
              alert.message = alert.message + ' ' + error.response.data;
            } else if (error?.response?.data?.message) {
              alert.message = error.response.data.message;
            } else if (
              error.response !== null &&
              error.response !== undefined &&
              error.response.data !== null &&
              error.response.data !== undefined &&
              error.response.data.errors !== null &&
              error.response.data.errors !== undefined
            ) {
              if (typeof error.response?.data.errors === typeof ResponseErrors) {
                // check for problem details object produced by built in .Net Core >3 model validation.
                (error.response.data.errors as ResponseErrors).DisplayName.forEach((element: string) => {
                  alert.message = alert.message + ' ' + element;
                });
              } else {
                (error.response.data.errors as string[]).forEach((element: string) => {
                  alert.message = alert.message + ' ' + element;
                });
              }
            }
          }
        }

        if (color) {
          alert.color = color;
        }
        if (timeout) {
          alert.timeout = timeout;
        }
        if (alert.showToast) {
          this.toastAlerts.push(alert);
        }
        this.busAlerts.push(alert);
        if (alert.timeout > 0) {
          setTimeout(() => {
            this.toastAlerts = this.toastAlerts.filter((x) => {
              return x !== alert;
            });
          }, alert.timeout);
        }
      }
    },

    async initializeStores() {
      await this.getActivityDefinitions();
      await this.getOrchestrationRouteModels();
      await this.getWorkflows();
    },

    login() {
      this.$auth.signIn();
    },

    logout() {
      this.$auth.signOut();
    },

    removeAlert(alert: Alert): void {
      this.toastAlerts = this.toastAlerts.filter((x) => x !== alert);
    },

    async signedIn() {
        let token = this.$auth.getToken();
        if (!token) return;
        let idTokenClaims = token.idTokenClaims as IdTokenClaims;
        if (!idTokenClaims) return;

        this.isAuthenticated = true;
        this.mini = !this.isAuthenticated;

        this.$http.defaults.headers.common = {
          Authorization: `bearer ${token.idToken}`,
          AzureADId: idTokenClaims.sub,
        };

        // Fetch the API version right after setting headers
        await this.$store.dispatch('getVersion', { $http: this.$http });

        this.initializeStores();
        this.updateProfile(idTokenClaims);
        this.checkEditorStatus(idTokenClaims.mail);
        this.checkQueueEditorStatus(idTokenClaims.mail);
        
        if (this.$route.path.startsWith('/login')) {
          this.$router.push({ path: '/modelpipeline' });
        }
    },
  },

  beforeDestroy() {
    this.$root.$off('theme-changed');
  },

  destroyed() {
    this.$auth.removeListener('loginEvent', this.signedIn);
  },
});
export default App;
